import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import debounce from 'lodash.debounce';

import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@atom/components/common/dragAndDrop';
import SchemaDetailContext, {
  DragDropType,
} from '@atom/components/schemaDetail/SchemaDetailContext';
import { ATTRIBUTE_UPDATE } from '@atom/graph/schema';
import { Button, Icon, IconButton, TextField } from '@atom/mui';
import { AddIcon, CloseIcon, DragIndicatorIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { DataType } from '@atom/types/dataType';
import { SchemaTreeAttribute } from '@atom/types/schema';

import { updatePendingCreation } from '../subItemDetail/subItemDetailUtilities';

import {
  addEnumOption,
  mergePendingChanges,
  removeEnumOption,
  removeTempEnumIndex,
  reorderEnumOptions,
  updateEnumOption,
  updatePendingUpdates,
  updateTempEnumsIndexes,
} from './attributeDetailUtilities';

import classes from './attributeDetail.module.css';

const DEBOUNCE_TIME = 500;

const EnumBuilder = () => {
  const {
    schemaTree,
    selectedAttribute,
    setSelectedAttribute,
    selectedSubItem,
    selectedAttributeRoute,
    refetchSchemaTree,
    setPendingUpdates,
    pendingUpdates,
    setPendingCreations,
  } = useContext(SchemaDetailContext);

  // If pending changes exist, they will be merged with the
  // selected attribute data to display
  const mergedAttribute = mergePendingChanges(
    selectedSubItem.id,
    selectedAttribute,
    pendingUpdates,
  );

  const [enumeration, setEnumeration] = useState<string[]>(
    mergedAttribute.enumeration,
  );

  useEffect(() => {
    if (mergedAttribute.enumeration.length !== enumeration.length) {
      setEnumeration(mergedAttribute.enumeration);
    }
  }, [mergedAttribute.enumeration]);

  useEffect(() => {
    setEnumeration(mergedAttribute.enumeration);
  }, [mergedAttribute.id]);

  const isEnumSingle = mergedAttribute.dataType === DataType.ENUM_SINGLE;

  const [updateAttribute] = useMutation(ATTRIBUTE_UPDATE);

  // If attribute is a temp attribute, values must be updated in pendingCreations state
  const handlePendingCreationUpdate = (value: string[]) => {
    const updatedAttribute = { ...selectedAttribute, enumeration: value };

    setPendingCreations(prev =>
      updatePendingCreation(
        selectedSubItem.id,
        selectedAttributeRoute,
        prev,
        updatedAttribute,
      ),
    );

    setSelectedAttribute(updatedAttribute);
  };

  // If schema is published, changes must be saved to the pending updates / creations carts
  const handlePendingAttributeUpdate = (
    property: keyof SchemaTreeAttribute,
    value: any,
  ) => {
    setPendingUpdates(prev =>
      updatePendingUpdates(
        selectedSubItem.id,
        prev,
        mergedAttribute,
        property,
        value,
      ),
    );
  };

  const handleAttributeUpdate = async (value: string[]) => {
    setSelectedAttribute(prev => ({ ...prev, enumeration: value }));

    await updateAttribute({
      variables: {
        input: {
          schemaId: selectedSubItem.id,
          attributeGroupId: selectedAttributeRoute.attributeGroupId,
          attributeId: mergedAttribute.id,
          enumeration: value,
        },
      },
    });

    refetchSchemaTree();
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const newEnumeration = reorderEnumOptions(enumeration, result);

    setEnumeration(newEnumeration);

    // If the schema is already published, add changes to cart
    if (schemaTree.isPublished) {
      if (mergedAttribute.tempEnumsIndexes) {
        // This handles existing dropdowns option reorder with a new option on published schemas
        const newTempEnumsIndexes = updateTempEnumsIndexes(
          mergedAttribute.tempEnumsIndexes,
          result,
        );
        handlePendingAttributeUpdate('tempEnumsIndexes', newTempEnumsIndexes);
      } else if (mergedAttribute.isTempAttribute) {
        // This handles newly created dropdown reorder on new dropdowns on published schemas
        handlePendingCreationUpdate(newEnumeration);
      } else {
        // This handles existing dropdowns option reorder on published schemas
        handlePendingAttributeUpdate('enumeration', newEnumeration);
      }
      return;
    }

    await handleAttributeUpdate(newEnumeration);
  };

  const updateAttributeDebounced = useCallback(
    debounce((newEnumeration: string[]) => {
      handleAttributeUpdate(newEnumeration);
    }, DEBOUNCE_TIME),
    [mergedAttribute],
  );

  const onEnumChange = event => {
    const newEnumeration = updateEnumOption(
      enumeration,
      Number(event.target.id),
      event.target.value,
    );

    setEnumeration(newEnumeration);

    if (selectedAttribute.isTempAttribute) {
      handlePendingCreationUpdate(newEnumeration);
      return;
    }

    if (schemaTree.isPublished) {
      handlePendingAttributeUpdate('enumeration', newEnumeration);
      return;
    }

    if (!event.target.value) {
      updateAttributeDebounced.cancel();
    } else {
      updateAttributeDebounced(newEnumeration);
    }
  };

  const onEnumRemove = async (index: number) => {
    const newEnumeration = removeEnumOption(enumeration, index);

    if (selectedAttribute.isTempAttribute) {
      handlePendingCreationUpdate(newEnumeration);
    } else if (schemaTree.isPublished) {
      const newTempEnumsIndexes = removeTempEnumIndex(
        mergedAttribute.tempEnumsIndexes,
        index,
      );
      handlePendingAttributeUpdate('tempEnumsIndexes', newTempEnumsIndexes);
      handlePendingAttributeUpdate('enumeration', newEnumeration);
    } else {
      setEnumeration(newEnumeration);
      await handleAttributeUpdate(newEnumeration);
    }
  };

  const onEnumAdd = async () => {
    const newEnumeration = addEnumOption(enumeration, isEnumSingle);
    if (selectedAttribute.isTempAttribute) {
      handlePendingCreationUpdate(newEnumeration);
    } else if (schemaTree.isPublished) {
      handlePendingAttributeUpdate(
        'tempEnumsIndexes',
        new Set([
          ...(mergedAttribute.tempEnumsIndexes || []),
          newEnumeration.length - 1,
        ]),
      );
      handlePendingAttributeUpdate('enumeration', newEnumeration);
    } else {
      setEnumeration(newEnumeration);
      await handleAttributeUpdate(newEnumeration);
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="enumBuilder" type={DragDropType.ENUM_OPTION}>
        {enumeration.map((option: string, index: number) => {
          // For enumsingle, there is an uneditable empty option in 0 index
          if (isEnumSingle && index === 0) {
            return <div />;
          }

          // On published schema options cannot be edited unless they are either
          // a temp attribute, or a temp option
          const isDisabled =
            schemaTree.isPublished &&
            !selectedAttribute.isTempAttribute &&
            !mergedAttribute.tempEnumsIndexes?.has(index);

          const displayNumber = isEnumSingle ? index : index + 1;

          return (
            <Draggable key={index} draggableId={index.toString()} index={index}>
              <div className={classes.optionsInputContainer}>
                <Icon
                  icon={DragIndicatorIcon}
                  color={colors.neutral.silver}
                  sx={{ paddingRight: '0.5rem' }}
                />
                <div
                  className={classes.optionsNumber}
                >{`Option ${displayNumber}:`}</div>
                <TextField
                  key={index}
                  value={option}
                  onChange={onEnumChange}
                  id={index.toString()}
                  disabled={isDisabled}
                />
                <IconButton
                  onClick={() => onEnumRemove(index)}
                  size="small"
                  disabled={isDisabled}
                >
                  <Icon icon={CloseIcon} />
                </IconButton>
              </div>
            </Draggable>
          );
        })}
      </Droppable>
      <div className={classes.optionsInputContainer}>
        <Button
          color="primary"
          onClick={onEnumAdd}
          startIcon={<Icon icon={AddIcon} color={colors.brand.blue} />}
        >
          add option
        </Button>
      </div>
    </DragDropContext>
  );
};

export default EnumBuilder;
